<template>
  <div :class="{ 'sold-out': isSoldOut }">
    <div class="flex trip">
      <div
        :id="trip.departure_date"
        class="flex flex-col items-center justify-center p-6 date-part border-r border-black-30 text-center"
      >
        <p class="fs-32 font-medium">{{ tripDate }}</p>
        <p class="fs-20 -mt-2">{{ tripMonth }}</p>
        <p>{{ tripYear }}</p>
      </div>
      <div
        class="trip-detail-part flex-1 p-8 border-r border-black-30 mdsm:border-r-0"
      >
        <h4 class="fs-20 font-medium mb-3">{{ trip.itineraries.name }}</h4>
        <div class="flex items-center">
          <div class="flex-none flex items-center">
            <svg
              width="24"
              height="24"
              class="flex-none mr-2"
              :class="{ 'opacity-50': isSoldOut }"
            >
              <use xlink:href="/img/sprite.svg#ic_calendar"></use>
            </svg>
            {{ trip.itineraries.itinerary_length + 1 }} days /
            {{ trip.itineraries.itinerary_length }} nights
          </div>

          <div class="flex-none mx-4 text-black-light-5">•</div>

          <div class="flex-none flex items-center">
            <svg
              width="24"
              height="24"
              class="flex-none mr-2"
              :class="{ 'opacity-50': isSoldOut }"
            >
              <use xlink:href="/img/sprite.svg#ic_diving"></use>
            </svg>
            {{ trip.itineraries.dive_number }} dives
          </div>
        </div>

        <itinerary-details
          @enquire="handleEnquire"
          class="mt-3"
          :trip="trip"
          :supplier="supplier"
          :selected-time="selectedTime"
        />

        <div
          class="hidden mdsm:block mdsm:-mx-6 mt-6 p-6 pb-0 border-t border-black-30"
        >
          <div class="flex justify-end items-center">
            <div>
              <div
                v-if="trip.discount"
                class="text-black-light-5 text-right fs-14 old-price"
              >
                from
                <span class="font-medium">
                  ${{ lowestPrice.real_price | formatPrice }}
                </span>
                /guest
              </div>
              <div>
                from
                <span class="fs-20 font-medium">
                  ${{ lowestPrice.final_price | formatPrice }}
                </span>
                /guest
              </div>
            </div>

            <div class="text-right">
              <button
                type="button"
                class="btn btn-primary input-tall enquire-button ml-4"
                v-if="!isSoldOut"
                @click="handleEnquire"
              >
                Enquire
              </button>
            </div>
          </div>
          <div class="text-right pt-4 font-medium">
            <span :class="cabinAvailability.class">
              {{ cabinAvailability.text }}
            </span>
          </div>
        </div>
      </div>
      <div
        class="price-part border-r border-black-30 flex-none p-6 flex flex-col text-center justify-center mdsm:hidden"
      >
        <div v-if="lowestPrice">
          <div
            v-if="lowestPrice.discount"
            class="text-black-light-5 text-center fs-14 old-price"
          >
            <currency-context>
              <template v-slot="{ convert }">
                <span
                  class="font-medium"
                  v-text="convert(lowestPrice.real_price)"
                ></span>
              </template>
            </currency-context>
          </div>
          <div class="mb-2">
            from
            <currency-context>
              <template v-slot="{ convert }">
                <span
                  class="fs-20 font-medium"
                  v-text="convert(lowestPrice.final_price)"
                ></span>
              </template>
            </currency-context>
          </div>
          <div
            class="addons fs-14 flex justify-center"
            v-if="hasDiscount || trip.itineraries.highlight_tag"
            :class="`addons-${highlightType}`"
          >
            <template v-if="!hasDiscount">
              <span class="fs-12 highlight-tag py-1 px-2">
                {{ trip.itineraries.highlight_tag }}
              </span>
            </template>
            <template v-else>
              <span
                class="fs-12 highlight-tag py-1 px-2"
                v-if="trip.discount_method === 'percentage'"
              >
                -{{ trip.discount }}% discount
              </span>
              <span
                class="fs-12 highlight-tag py-1 px-2"
                v-else-if="trip.discount_method === 'fix_amount'"
              >
                -
                <currency-context>
                  <template v-slot="{ convert }">
                    <span v-text="convert(trip.discount)"></span>
                  </template>
                </currency-context>
                off
              </span>
              <span
                class="fs-12 highlight-tag py-1 px-2"
                v-else-if="trip.discount_method === 'custom'"
              >
                Special rate
              </span>
            </template>
          </div>
        </div>
      </div>
      <div
        class="action-part flex-none p-6 flex flex-col text-center justify-center mdsm:hidden"
      >
        <button
          type="button"
          class="btn btn-secondary input-tall w-full mt-4 mx-auto enquire-button mb-3"
          v-if="!isSoldOut"
          @click="handleEnquire"
        >
          Select cabin
        </button>
        <p class="text-center font-medium fs-14">
          <span :class="cabinAvailability.class">
            {{ cabinAvailability.text }}
          </span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import ItineraryDetails from "./ItineraryDetails";
import TripMixin from "./TripMixin";
import CurrencyContext from "../../components/core/CurrencyContext";

export default {
  mixins: [TripMixin],
  components: {
    ItineraryDetails,
    CurrencyContext,
  },
  props: ["trip", "selectedTime", "supplier"],
  mounted() {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has("from")) {
      const from = searchParams.get("from");
      const element = document.getElementById(from);
      if (element) {
        window.scrollTo({
          top: element.offsetTop,
          behavior: "smooth",
          block: "end",
        });
      }
    }
  },
  methods: {
    handleEnquire() {
      this.$emit("enquire-click");
    },
  },
};
</script>

<style scoped>
.highlight-tag {
  font-weight: 500;
  border-radius: 2px;
  color: white;
  padding-bottom: 5px;
  padding-top: 5px;
}

.trip {
  border-bottom: 1px solid var(--border-primary);
}

.addons + .trip {
  border-top-left-radius: 0px;
  border-top-right-radius: 0px;
  border-top: none;
}

.sold-out .addons.addons-discount .highlight-tag {
  background: var(--purple-light-4);
}

.addons.addons-discount .highlight-tag {
  background: var(--discount-primary);
}

.addons.addons-blue {
  background: var(--info-basis-bg);
  border: 1px solid var(--blue-light-3);
}

.addons.addons-blue + .trip {
  border-color: var(--blue-light-3);
}

.addons.addons-blue .highlight-tag {
  background: var(--info-primary);
}

.sold-out .addons.addons-blue .highlight-tag {
  background: var(--blue-light-4);
}

.addons.addons-green {
  background: var(--success-basis-bg);
  border: 1px solid var(--green-light-3);
}

.addons.addons-green + .trip {
  border-color: var(--green-light-3);
}

.addons.addons-green .highlight-tag {
  background: var(--success-primary);
}

.sold-out .addons.addons-green .highlight-tag {
  background: var(--green-light-4);
}

.addons.addons-marine {
  background: var(--marine-light-1);
  border: 1px solid var(--marine-light-3);
}

.addons.addons-marine + .trip {
  border-color: var(--marine-light-3);
}

.addons.addons-marine .highlight-tag {
  background: var(--marine);
}

.highlight-tag[data-v-15146133] {
  background: var(--discount-primary);
}

.price-part {
  min-width: 185px;
  max-width: 185px;
}

.action-part {
  min-width: 248px;
}

.old-price {
  text-decoration: line-through;
}

.enquire-button {
  width: 200px;
}

.fill-discount {
  fill: var(--discount-primary);
}

.trip-highlight-pill {
  border-radius: 2px;
  font-weight: bold;
}

@media screen and (min-width: 601px) and (max-width: 1200px) {
  .enquire-button {
    width: 175px;
  }
}
</style>
